/**
 * The plan-limit refusal in words (docs/platform-standards.md §7 "Plan
 * limits"; keepr-api docs/SCHEMA.md "Plan limits").
 *
 * The API answers an add past a plan limit with 409 `plan_limit` and tells
 * each caller only what is theirs to know:
 *
 *   the collection's owner     meter, limit, used, plan, lifts
 *   anyone else signed in      meter, limit
 *   no account (a visitor)     meter
 *
 * So the AUDIENCE is read from the payload itself, never from the client's
 * idea of who owns what: `plan` or `used` means the owner, `limit` someone
 * signed in, neither a visitor. The words never name a plan — not even to the
 * owner (the plan's name is the Plan section's, behind config/plans.js).
 */

const NUMBER = new Intl.NumberFormat('en-US');
const MB = 1024 * 1024;
const GB = 1024 * MB;

const METER_WORDS = {
	items: ['item', 'items'],
	rules: ['active rule', 'active rules'],
	people: ['person shared with', 'people shared with'],
	forms: ['permanent form', 'permanent forms'],
	submissions: ['submission this month', 'submissions this month'],
};

/** "2 GB", "500 MB" — storage in the binary units the API counts in. */
export function formatBytes(bytes) {
	if (!Number.isFinite(bytes) || bytes < 0) return '';
	if (bytes >= GB) {
		const gb = bytes / GB;
		return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
	}
	const mb = bytes / MB;
	if (mb >= 1) return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
	return `${NUMBER.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/** "10,000 items", "2 GB of storage", "1 permanent form". */
export function meterAmount(meter, amount) {
	if (meter === 'storage') return `${formatBytes(amount)} of storage`;
	const words = METER_WORDS[meter];
	if (!words) return NUMBER.format(amount);
	return `${NUMBER.format(amount)} ${amount === 1 ? words[0] : words[1]}`;
}

/** What a meter is called on its own, in sentence case ("Storage", "Items"). */
export const METER_LABELS = Object.freeze({
	storage: 'Storage',
	items: 'Items',
	rules: 'Active rules',
	people: 'People shared with',
	forms: 'Permanent forms',
	submissions: 'Submissions this month',
});

/**
 * The refusal's payload when `error` is a 409 `plan_limit` (an axios error or
 * the response body itself), else null.
 */
export function planLimitOf(error) {
	const data = error?.response ? error.response.data : error;
	// A response that states another status is not this refusal; a body
	// passed on its own (a preview's `refusal`) carries no status at all.
	const status = error?.response ? error.response.status : undefined;
	if (!data || typeof data !== 'object' || data.code !== 'plan_limit') return null;
	if (status !== undefined && status !== 409) return null;
	return data;
}

/** 'owner' | 'member' | 'visitor', from what the API chose to send. */
export function planLimitAudience(refusal) {
	if (!refusal) return null;
	if (refusal.plan !== undefined || refusal.used !== undefined) return 'owner';
	if (refusal.limit !== undefined) return 'member';
	return 'visitor';
}

export const STILL_WORKS = 'Reading, editing, exporting and deleting still work.';
export const ASK_OWNER = "Ask the collection's owner.";
export const VISITOR_WORDS = "This form isn't taking submissions right now.";

/**
 * `{ audience, title, detail }` for a plan-limit refusal:
 *
 *   owner    "You've reached the limit of 10,000 items." / still works
 *   member   "This collection has reached its limit of 10,000 items." / ask the owner
 *   visitor  "This form isn't taking submissions right now." / nothing else
 *
 * A limit the payload does not state (a malformed body) reads "a limit".
 */
export function planLimitWords(refusal) {
	const audience = planLimitAudience(refusal);
	if (!audience) return null;
	if (audience === 'visitor') return { audience, title: VISITOR_WORDS, detail: null };
	const known = Number.isFinite(refusal.limit) && (refusal.meter === 'storage' || METER_WORDS[refusal.meter] !== undefined);
	const amount = known ? `its limit of ${meterAmount(refusal.meter, refusal.limit)}` : 'a limit';
	if (audience === 'owner') {
		return {
			audience,
			title: known ? `You've reached the limit of ${meterAmount(refusal.meter, refusal.limit)}.` : "You've reached a limit.",
			detail: STILL_WORKS,
		};
	}
	return {
		audience,
		title: `This collection has reached ${amount}.`,
		detail: `${STILL_WORKS} ${ASK_OWNER}`,
	};
}

/** The refusal as one sentence, for a toast or an inline line. */
export function planLimitSentence(refusal) {
	const words = planLimitWords(refusal);
	if (!words) return null;
	return words.detail ? `${words.title} ${words.detail}` : words.title;
}
